import type { BehaviorStepType } from 'types/api/diagnosisReportRefResponseType';
import { formatNumberToCurrency, formatRateToPercent } from 'utils/app';

import type { JSX } from 'react';

/**
 * ステップ別ファネル表示コンポーネント（EV-report-analytics-02）。
 * 到達セッション数と直前ステップからの離脱率を横棒で表示する。
 */
export default function WsReportStepFunnel(props: {
  steps: BehaviorStepType[];
}): JSX.Element | null {
  const { steps } = props;
  if (steps.length === 0) return null;

  const firstReachedCount = steps[0].reachedCount;

  // 先頭を除いた中での最大離脱率とその位置（同値なら最も上のものを優先）
  let maxDropoutRate = 0;
  let highlightIndex = -1;
  steps.forEach((step, index) => {
    if (index === 0) return;
    if (step.dropoutRate !== null && step.dropoutRate > maxDropoutRate) {
      maxDropoutRate = step.dropoutRate;
      highlightIndex = index;
    }
  });
  // 全員完走（最大離脱率が 0）のときは強調しない
  if (maxDropoutRate === 0) highlightIndex = -1;

  return (
    <div className="card card-white behavior-analytics-card">
      <div className="card-body">
        <h4 className="title-section title-sm">ステップ別ファネル</h4>
        {steps.map((step, index) => {
          const barWidthPercent =
            firstReachedCount === 0 ? 0 : (step.reachedCount / firstReachedCount) * 100;
          const highlighted = index === highlightIndex;
          const dropoutRatePercent =
            step.dropoutRate !== null ? formatRateToPercent(step.dropoutRate) : null;

          return (
            <div key={step.pageNo}>
              <div className="behavior-funnel-line">
                <span className="behavior-funnel-line-label" title={step.label}>
                  {step.label}
                </span>
                <div className="behavior-funnel-line-track">
                  <div
                    data-testid="behavior-funnel-bar-fill"
                    className={
                      highlighted
                        ? 'behavior-bar-fill behavior-bar-fill--highlight'
                        : 'behavior-bar-fill'
                    }
                    style={{ width: `${barWidthPercent}%` }}
                  />
                </div>
                <span
                  className={
                    highlighted
                      ? 'behavior-funnel-line-count behavior-highlight-text'
                      : 'behavior-funnel-line-count'
                  }
                >
                  {formatNumberToCurrency(step.reachedCount)}
                </span>
                <span
                  className={
                    highlighted
                      ? 'behavior-funnel-line-rate behavior-highlight-text'
                      : 'behavior-funnel-line-rate'
                  }
                >
                  {dropoutRatePercent !== null &&
                    `${dropoutRatePercent === 0 ? '' : '-'}${dropoutRatePercent}%`}
                </span>
              </div>
              {highlighted && <p className="behavior-highlight-text">最も離脱が多いステップです</p>}
            </div>
          );
        })}
        <p className="behavior-analytics-note">
          ステップ名は現在の設問構成に基づいて表示しています。公開後に設問や取得項目を増減した場合、変更前の期間のステップ名は現在の構成で表示されます。
        </p>
      </div>
    </div>
  );
}
